'use client'

import React, { useState, forwardRef } from 'react'

import {
  baseStyles,
  contentStyles,
  disclaimerStyles,
  dismissIconStyles,
  iconStyles,
  messageStyles,
  titleStyles,
} from './styles'
import { Icon, IconSize } from '@sidecar-components/atoms/Icon'
import { Button } from '@sidecar-components/atoms/Button'
import { ButtonContainer } from '@sidecar-components/atoms/ButtonContainer'
import { IconButton } from '@sidecar-components/molecules/IconButton'
import { Sentiment } from '@sidecar-components/types'
import { BubbleProps } from './Bubble.types'

export const Bubble = forwardRef(
  (
    {
      children,
      className = '',
      contentClass = '',
      icon = 'exclamation',
      id,
      canDismiss = true,
      title = '',
      message = '',
      sentiment = Sentiment.ACCENT,
      testId = '',
      disclaimer = '',
      buttons = [],
    }: BubbleProps,
    forwardedRef: any,
  ) => {
    const [dismissed, setDismissed] = useState(false)

    //TODO: consider pulling this out into a util
    const formatId = (str: string) => {
      return str.toLowerCase().split(' ').join('-')
    }

    // The role is useful for screen readers to know the type of alert
    let role
    switch (sentiment) {
      case Sentiment.ACCENT:
        role = 'status'
        break
      case Sentiment.NOTIFY:
        role = 'status'
        break
      case Sentiment.RISK:
        role = 'alert'
        break
    }

    // Figuring out the unique part of the ID is something that each element will
    // end up needing, so do that here and then share it. Since this is a nested
    // ternary, the logic is: use the ID, otherwise use the title, otherwise use
    // the message itself. Since titles and message might be long af, we format
    // them to be kebab case (should probably also shorten or encode them to not
    // be so messy).
    const uniqueId = id ? id : title ? formatId(title) : formatId(message)

    return (
      <div
        className={`${baseStyles({
          sentiment,
          dismissed,
          icon,
        })} ${className}`}
        role={role}
        aria-describedby={title ? `${uniqueId}-title` : `${uniqueId}-message`}
        ref={forwardedRef}
        data-testid={testId}
      >
        {/* allow for no icon, passing in an empty string would cause an error message */}
        {icon && (
          <Icon
            name={icon}
            size={IconSize.SM}
            className={iconStyles(sentiment)}
          />
        )}

        <div className={`${contentStyles} ${contentClass}`}>
          {!!title && (
            <h4 id={`${uniqueId}-title`} className={titleStyles(sentiment)}>
              {title}
            </h4>
          )}

          {/* If the bubble is given children, it's most likely going to be
            multiple elements and therefor a div is a more appropriate
            container. If it's just a message string, a <p> is more suited */}
          {children ? (
            <div
              id={`${uniqueId}-message`}
              className={messageStyles(sentiment)}
            >
              {children}
            </div>
          ) : (
            <p id={`${uniqueId}-message`} className={messageStyles(sentiment)}>
              {message}
            </p>
          )}

          {buttons.length > 0 && (
            <ButtonContainer>
              {buttons.map((button, i) => (
                <Button
                  data-testid={button.testId}
                  key={i}
                  icon={button.icon}
                  iconPosition={button.iconPosition}
                  sentiment={button.sentiment}
                  onClick={button.onClick}
                >
                  {button.children}
                </Button>
              ))}
            </ButtonContainer>
          )}
          {!!disclaimer && (
            <p className={disclaimerStyles(sentiment)}>{disclaimer}</p>
          )}
        </div>

        {!!canDismiss && (
          <IconButton
            icon="x"
            size={IconSize.SM}
            sentiment={sentiment}
            className={dismissIconStyles}
            onClick={() => setDismissed(true)}
            label="Dismiss"
          />
        )}
      </div>
    )
  },
)
